import type { MaintenancePreviewGroup } from '@/hooks/useRuns';

/**
 * Auswahl-Logik der Komplettwartung ("Kunde komplett warten").
 *
 * Bewusst reine Funktionen ohne React: die Zähler in der Zusammenfassung und
 * der Request-Body von POST /runs/batch müssen aus derselben Quelle kommen,
 * sonst zeigt das UI etwas anderes an, als es startet.
 *
 * Record-Schlüssel ist der Routine-Code, Wert sind die angewählten
 * System-IDs. Fehlender Schlüssel und leeres Array bedeuten dasselbe:
 * für diese Routine wird kein Lauf angelegt.
 */

export type BatchSelection = Record<string, string[]>;

export interface BatchItem {
  routineSlug: string;
  systemIds: string[];
}

export interface BatchCounts {
  /** Anzahl Läufe = Gruppen mit mindestens einem System. */
  runs: number;
  /** Verschiedene Systeme über alle Gruppen (ein System kann mehrfach vorkommen). */
  systems: number;
  /** Summe aller System-Zuweisungen; > systems wenn Routinen sich überschneiden. */
  assignments: number;
}

export type GroupState = 'all' | 'some' | 'none';

/**
 * Startauswahl: alles angehakt AUSSER Routinen mit `auto_select: false`.
 *
 * Die bleiben sichtbar, aber leer vorausgewählt - sonst läuft ein
 * `apt full-upgrade` mit, das Pakete entfernen darf, nur weil jemand
 * "alles warten" gedrückt hat (genau so passiert am 12.08.2026).
 */
export function initialSelection(
  groups: readonly MaintenancePreviewGroup[]
): BatchSelection {
  const out: BatchSelection = {};
  for (const group of groups) {
    out[group.routine.code] =
      group.routine.auto_select === false ? [] : group.systems.map((s) => s.id);
  }
  return out;
}

export function selectedIds(selection: BatchSelection, code: string): string[] {
  return selection[code] ?? [];
}

export function groupState(
  selection: BatchSelection,
  group: MaintenancePreviewGroup
): GroupState {
  const count = selectedIds(selection, group.routine.code).length;
  if (count === 0) return 'none';
  return count >= group.systems.length ? 'all' : 'some';
}

/** Ganze Gruppe an- oder abwählen; teilweise gewählt zählt als "an" (=> aus). */
export function toggleGroup(
  selection: BatchSelection,
  group: MaintenancePreviewGroup
): BatchSelection {
  const code = group.routine.code;
  const current = selectedIds(selection, code);
  return {
    ...selection,
    [code]: current.length > 0 ? [] : group.systems.map((s) => s.id),
  };
}

/**
 * Einzelnes System innerhalb einer Gruppe umschalten. Die Reihenfolge folgt
 * immer der Vorschau, damit derselbe Klickpfad denselben Body erzeugt - sonst
 * trägt der Idempotency-Key bei einem Retry einen anderen Request.
 */
export function toggleSystem(
  selection: BatchSelection,
  group: MaintenancePreviewGroup,
  systemId: string
): BatchSelection {
  const code = group.routine.code;
  const current = selectedIds(selection, code);
  const next = current.includes(systemId)
    ? current.filter((id) => id !== systemId)
    : [...current, systemId];
  const order = group.systems.map((s) => s.id);
  next.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  return { ...selection, [code]: next };
}

export function selectAll(groups: readonly MaintenancePreviewGroup[]): BatchSelection {
  return initialSelection(groups);
}

export function clearAll(groups: readonly MaintenancePreviewGroup[]): BatchSelection {
  const out: BatchSelection = {};
  for (const group of groups) out[group.routine.code] = [];
  return out;
}

export function selectedItems(
  groups: readonly MaintenancePreviewGroup[],
  selection: BatchSelection
): BatchItem[] {
  const items: BatchItem[] = [];
  for (const group of groups) {
    const ids = selectedIds(selection, group.routine.code);
    if (ids.length > 0) items.push({ routineSlug: group.routine.code, systemIds: ids });
  }
  return items;
}

export function selectionCounts(
  groups: readonly MaintenancePreviewGroup[],
  selection: BatchSelection
): BatchCounts {
  const unique = new Set<string>();
  let runs = 0;
  let assignments = 0;
  for (const group of groups) {
    const ids = selectedIds(selection, group.routine.code);
    if (ids.length === 0) continue;
    runs += 1;
    assignments += ids.length;
    for (const id of ids) unique.add(id);
  }
  return { runs, systems: unique.size, assignments };
}

/**
 * Harte Grenzen des Endpunkts (POST /runs/batch). Sie stehen hier, weil die
 * Vorschau sie NICHT kennt: sie liefert jede passende Routine mit allen
 * passenden Systemen, und die Erstauswahl hängt alles an. Ein Kunde mit >200
 * Systemen an einer Routine erzeugt damit eine Auswahl, die der Server mit 422
 * ablehnt (rohe Pydantic-Meldung) - der Nutzer sähe nur "Eingaben sind
 * ungültig" und wüsste nicht, was zu tun ist.
 *
 * 200 ist keine Batch-Eigenheit, sondern das Systemlimit EINES Runs (identisch
 * in POST /runs) - Aufteilen ist die einzige Lösung, nicht Hochsetzen.
 */
export const MAX_SYSTEMS_PER_RUN = 200;
export const MAX_RUNS_PER_BATCH = 50;
export const MAX_ASSIGNMENTS_PER_BATCH = 1000;

/**
 * Was würde der Server an dieser Auswahl ablehnen? Leer = startklar.
 * Deutscher Klartext samt Auswegs - der Nutzer muss wissen, was er abwählen
 * soll, nicht nur dass etwas zu groß ist.
 */
export function selectionProblems(
  groups: readonly MaintenancePreviewGroup[],
  selection: BatchSelection
): string[] {
  const problems: string[] = [];
  for (const group of groups) {
    const count = selectedIds(selection, group.routine.code).length;
    if (count > MAX_SYSTEMS_PER_RUN) {
      problems.push(
        `${group.routine.display_name}: ${count} Systeme ausgewählt, ein Lauf trägt ` +
          `höchstens ${MAX_SYSTEMS_PER_RUN}. Systeme abwählen und den Rest in einem ` +
          `zweiten Vorgang starten.`
      );
    }
  }
  const counts = selectionCounts(groups, selection);
  if (counts.runs > MAX_RUNS_PER_BATCH) {
    problems.push(
      `${counts.runs} Routinen ausgewählt, höchstens ${MAX_RUNS_PER_BATCH} pro Vorgang. ` +
        `Routinen abwählen und in mehreren Vorgängen starten.`
    );
  }
  if (counts.assignments > MAX_ASSIGNMENTS_PER_BATCH) {
    problems.push(
      `${counts.assignments} Systemzuweisungen über alle Routinen, höchstens ` +
        `${MAX_ASSIGNMENTS_PER_BATCH} pro Vorgang. Routinen oder Systeme abwählen.`
    );
  }
  return problems;
}

/** Gilt für den ganzen Batch: ein Snapshot-Flag deckt alle Läufe ab. */
export function anySelectedRoutine(
  groups: readonly MaintenancePreviewGroup[],
  selection: BatchSelection,
  predicate: (routine: MaintenancePreviewGroup['routine']) => boolean
): boolean {
  return groups.some(
    (g) => selectedIds(selection, g.routine.code).length > 0 && predicate(g.routine)
  );
}
